import React, { useState } from 'react';
import { X, Upload, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const UploadDocumentsModal = ({ clientId, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const documentTypes = [
    'Application',
    "Driver's License",
    'Voided Check',
    'Bank Statements',
    'Funding Call',
    'Contract',
    'Tax Returns',
    'Financial Statements',
    'Other'
  ];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const filesWithTypes = selectedFiles.map(file => ({
      file,
      docType: 'Application',
      id: Math.random().toString(36)
    }));
    setFiles([...files, ...filesWithTypes]);
  };

  const updateDocType = (id, docType) => {
    setFiles(files.map(f => f.id === id ? { ...f, docType } : f));
  };

  const removeFile = (id) => {
    setFiles(files.filter(f => f.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast({ title: 'Error', description: 'Please select files to upload', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      
      for (const fileData of files) {
        const formData = new FormData();
        formData.append('files', fileData.file);
        formData.append('document_type', fileData.docType);

        await axios.post(`${API_BASE}/clients/${clientId}/documents`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast({ title: 'Success', description: `${files.length} document(s) uploaded successfully` });
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload documents', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Upload Documents</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select Files</label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full border rounded p-2 text-sm"
            />
          </div>

          {files.length > 0 && (
            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold text-sm">Files to Upload ({files.length})</h3>
              {files.map((fileData) => (
                <div key={fileData.id} className="flex items-center gap-3 p-3 border rounded bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{fileData.file.name}</p>
                    <p className="text-xs text-gray-500">{(fileData.file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <select
                    value={fileData.docType}
                    onChange={(e) => updateDocType(fileData.id, e.target.value)}
                    className="border rounded p-2 text-sm"
                  >
                    {documentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeFile(fileData.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button 
              type="submit" 
              disabled={uploading || files.length === 0} 
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Upload size={16} className="mr-2" />
              {uploading ? 'Uploading...' : `Upload ${files.length} Document(s)`}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose} 
              className="flex-1" 
              disabled={uploading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDocumentsModal;
